/** @format */

import { AuditActionType, ResourceType, RiskLevel } from '../types/auditLog';
import { getApiOrigin } from '@utils/apiOrigin';

/**
 * HIPAA Audit Constants
 * Configuration for audit logging system
 */

// Minimum retention period for audit logs (in years)
export const AUDIT_LOG_RETENTION_YEARS = 6;

// Risk level mapping for different actions
export const ACTION_RISK_LEVELS: Record<AuditActionType, RiskLevel> = {
  // Authentication - Medium to High
  [AuditActionType.LOGIN]: RiskLevel.MEDIUM,
  [AuditActionType.LOGOUT]: RiskLevel.LOW,
  [AuditActionType.LOGIN_FAILED]: RiskLevel.HIGH,
  [AuditActionType.PASSWORD_RESET]: RiskLevel.HIGH,
  [AuditActionType.PASSWORD_CHANGE]: RiskLevel.MEDIUM,
  [AuditActionType.OTP_SEND]: RiskLevel.MEDIUM,
  [AuditActionType.OTP_VERIFY]: RiskLevel.MEDIUM,
  [AuditActionType.SESSION_TIMEOUT]: RiskLevel.LOW,

  // CRUD - Low to Medium
  [AuditActionType.CREATE]: RiskLevel.MEDIUM,
  [AuditActionType.READ]: RiskLevel.LOW,
  [AuditActionType.UPDATE]: RiskLevel.MEDIUM,
  [AuditActionType.DELETE]: RiskLevel.HIGH,

  // Access Actions - Low to High
  [AuditActionType.VIEW]: RiskLevel.LOW,
  [AuditActionType.DOWNLOAD]: RiskLevel.HIGH,
  [AuditActionType.UPLOAD]: RiskLevel.MEDIUM,
  [AuditActionType.SEARCH]: RiskLevel.LOW,
  [AuditActionType.FILTER]: RiskLevel.LOW,
  [AuditActionType.SAVE]: RiskLevel.MEDIUM,
  // Removed action types (commented out):
  // [AuditActionType.ACCESS]: RiskLevel.MEDIUM, // Removed - use VIEW instead
  // [AuditActionType.EXPORT]: RiskLevel.HIGH, // Removed - use DOWNLOAD instead
  // [AuditActionType.PRINT]: RiskLevel.HIGH, // Removed
  // [AuditActionType.DOCUMENT_UPLOAD]: RiskLevel.MEDIUM, // Removed - use UPLOAD instead
  // [AuditActionType.DOCUMENT_DOWNLOAD]: RiskLevel.HIGH, // Removed - use DOWNLOAD instead
  // [AuditActionType.DOCUMENT_VIEW]: RiskLevel.MEDIUM, // Removed - use VIEW instead
  // [AuditActionType.DOCUMENT_DELETE]: RiskLevel.HIGH, // Removed - use DELETE instead
  // [AuditActionType.DOCUMENT_SHARE]: RiskLevel.HIGH, // Removed - use PERMISSION_GRANT instead

  // Administrative - High to Critical
  [AuditActionType.USER_INVITE]: RiskLevel.HIGH,
  [AuditActionType.USER_ROLE_CHANGE]: RiskLevel.CRITICAL,
  [AuditActionType.USER_DEACTIVATE]: RiskLevel.CRITICAL,
  [AuditActionType.PERMISSION_GRANT]: RiskLevel.CRITICAL,
  [AuditActionType.PERMISSION_REVOKE]: RiskLevel.CRITICAL,

  // Audit Access - Critical
  [AuditActionType.AUDIT_LOG_ACCESS]: RiskLevel.CRITICAL,
  [AuditActionType.AUDIT_LOG_EXPORT]: RiskLevel.CRITICAL,
  [AuditActionType.CONNECT_WITH_AFFABLEFLOW]: RiskLevel.MEDIUM,
  [AuditActionType.UPLOAD_FROM_AFFABLE]: RiskLevel.MEDIUM,
};

// Resource types that contain ePHI (require special handling)
export const EPHI_RESOURCE_TYPES: ResourceType[] = [
  ResourceType.FACILITY,
  ResourceType.FACILITY_DETAILS,
  ResourceType.FACILITY_LIST,
  ResourceType.COMPLIANCE_DATA,
  ResourceType.COMPLIANCE_REPORT,
  ResourceType.REGULATORY_REQUIREMENT,
  ResourceType.AUDIT_CHECKLIST,
  ResourceType.USER_PROFILE,
  ResourceType.USER_LIST,
  ResourceType.RISK_ANALYSIS,
  ResourceType.MOCK_SURVEY,
  ResourceType.SURVEY_VIEWER,
];

// Actions that require access reason
export const REQUIRE_ACCESS_REASON: AuditActionType[] = [
  AuditActionType.DOWNLOAD,
  // AuditActionType.EXPORT, // Removed - use DOWNLOAD instead
  // AuditActionType.DOCUMENT_DOWNLOAD, // Removed - use DOWNLOAD instead
  AuditActionType.AUDIT_LOG_ACCESS,
  AuditActionType.AUDIT_LOG_EXPORT,
  AuditActionType.USER_ROLE_CHANGE,
  AuditActionType.PERMISSION_GRANT,
  AuditActionType.PERMISSION_REVOKE,
];

// Actions that trigger alerts
export const ALERT_ACTIONS: AuditActionType[] = [
  AuditActionType.LOGIN_FAILED,
  AuditActionType.DELETE,
  AuditActionType.DOWNLOAD,
  // AuditActionType.EXPORT, // Removed - use DOWNLOAD instead
  AuditActionType.USER_ROLE_CHANGE,
  AuditActionType.USER_DEACTIVATE,
  AuditActionType.PERMISSION_GRANT,
  AuditActionType.PERMISSION_REVOKE,
  AuditActionType.AUDIT_LOG_ACCESS,
  AuditActionType.AUDIT_LOG_EXPORT,
];

// Human-readable labels for action types
export const ACTION_TYPE_LABELS: Record<AuditActionType, string> = {
  [AuditActionType.LOGIN]: 'User Login',
  [AuditActionType.LOGOUT]: 'User Logout',
  [AuditActionType.LOGIN_FAILED]: 'Failed Login Attempt',
  [AuditActionType.PASSWORD_RESET]: 'Password Reset',
  [AuditActionType.PASSWORD_CHANGE]: 'Password Change',
  [AuditActionType.OTP_SEND]: 'OTP Sent',
  [AuditActionType.OTP_VERIFY]: 'OTP Verified',
  [AuditActionType.SESSION_TIMEOUT]: 'Session Timeout',

  [AuditActionType.CREATE]: 'Create Record',
  [AuditActionType.READ]: 'Read Record',
  [AuditActionType.UPDATE]: 'Update Record',
  [AuditActionType.DELETE]: 'Delete Record',

  [AuditActionType.VIEW]: 'View Resource',
  [AuditActionType.DOWNLOAD]: 'Download Data',
  [AuditActionType.UPLOAD]: 'Upload Data',
  [AuditActionType.SEARCH]: 'Search',
  [AuditActionType.FILTER]: 'Apply Filter',
  [AuditActionType.SAVE]: 'Save Data',
  // Removed action type labels (commented out):
  // [AuditActionType.ACCESS]: 'Access Resource', // Removed - use VIEW instead
  // [AuditActionType.EXPORT]: 'Export Data', // Removed - use DOWNLOAD instead
  // [AuditActionType.PRINT]: 'Print Data', // Removed
  // [AuditActionType.DOCUMENT_UPLOAD]: 'Upload Document', // Removed - use UPLOAD instead
  // [AuditActionType.DOCUMENT_DOWNLOAD]: 'Download Document', // Removed - use DOWNLOAD instead
  // [AuditActionType.DOCUMENT_VIEW]: 'View Document', // Removed - use VIEW instead
  // [AuditActionType.DOCUMENT_DELETE]: 'Delete Document', // Removed - use DELETE instead
  // [AuditActionType.DOCUMENT_SHARE]: 'Share Document', // Removed - use PERMISSION_GRANT instead

  [AuditActionType.USER_INVITE]: 'Invite User',
  [AuditActionType.USER_ROLE_CHANGE]: 'Change User Role',
  [AuditActionType.USER_DEACTIVATE]: 'Deactivate User',
  [AuditActionType.PERMISSION_GRANT]: 'Grant Permission',
  [AuditActionType.PERMISSION_REVOKE]: 'Revoke Permission',

  [AuditActionType.AUDIT_LOG_ACCESS]: 'Access Audit Logs',
  [AuditActionType.AUDIT_LOG_EXPORT]: 'Export Audit Logs',
  [AuditActionType.CONNECT_WITH_AFFABLEFLOW]: 'Connect with AffableFLOW',
  [AuditActionType.UPLOAD_FROM_AFFABLE]: 'Upload from Affable',
};

// Human-readable labels for resource types
export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  // 1. Facility Analysis
  [ResourceType.FACILITY]: 'Facility',
  [ResourceType.FACILITY_DETAILS]: 'Facility Details',
  [ResourceType.FACILITY_LIST]: 'Facility List',
  [ResourceType.FACILITY_ANALYSIS]: 'Facility Analysis',

  // 2. Potential Penalties
  [ResourceType.PENALTY_CALCULATION]: 'Penalty Calculation',
  [ResourceType.POTENTIAL_PENALTIES]: 'Potential Penalties',

  // 3. Exclusion / Sanction Check
  [ResourceType.EXCLUSION_SANCTION_CHECK]: 'Exclusion / Sanction Check',

  // 4. Continuous Compliance
  [ResourceType.COMPLIANCE_DATA]: 'Compliance Data',
  [ResourceType.COMPLIANCE_REPORT]: 'Compliance Report',
  [ResourceType.REGULATORY_REQUIREMENT]: 'Regulatory Requirement',
  [ResourceType.AUDIT_CHECKLIST]: 'Audit Checklist',
  [ResourceType.CONTINUOUS_COMPLIANCE]: 'Continuous Compliance',

  // 5. Risk Analysis
  [ResourceType.RISK_ANALYSIS]: 'Risk Analysis',

  // 6. Mock Survey
  [ResourceType.MOCK_SURVEY]: 'Mock Survey',

  // 7. Survey Viewer from Mock survey screen
  [ResourceType.SURVEY_VIEWER]: 'Survey Viewer',

  // 8. User Management
  [ResourceType.USER_PROFILE]: 'User Profile',
  [ResourceType.USER_LIST]: 'User List',
  [ResourceType.USER_INVITATION]: 'User Invitation',
  [ResourceType.USER_TYPE]: 'User Type',
  [ResourceType.USER_SUBTYPE]: 'User Subtype',
  [ResourceType.USER_MANAGEMENT]: 'User Management',

  // 9. Billings
  [ResourceType.BILLING_INFO]: 'Billing Information',
  [ResourceType.PAYMENT]: 'Payment',
  [ResourceType.PLAN]: 'Plan',
  [ResourceType.BILLINGS]: 'Billings',

  // 10. Audit Logs
  [ResourceType.AUDIT_LOG]: 'Audit Log',
  [ResourceType.AUDIT_REPORT]: 'Audit Report',
  [ResourceType.AUDIT_LOGS]: 'Audit Logs',

  // 11. Authentication / Session
  [ResourceType.USER]: 'User',
  [ResourceType.SESSION]: 'Session',
  [ResourceType.AUTHENTICATION]: 'Authentication',
  [ResourceType.AUTHENTICATION_SESSION]: 'Authentication / Session',

  // Discovery
  [ResourceType.DISCOVERY_CONTENT]: 'Discovery Content',

  [ResourceType.NETWORK]: 'Network',
};

// Audit log batch size for sending to backend
export const AUDIT_LOG_BATCH_SIZE = 10;

// Audit log batch timeout (milliseconds)
export const AUDIT_LOG_BATCH_TIMEOUT = 5000; // 5 seconds

// Enable console logging for audit events (development only)
export const AUDIT_DEBUG_MODE = process.env.NODE_ENV === 'development';

// Session ID generation and storage
export const SESSION_ID_KEY = 'audit_session_id';

// Placeholder backend URL (to be replaced with actual backend)
export const AUDIT_API_BASE_URL = getApiOrigin();
export const AUDIT_LOGS_API_URL = `${AUDIT_API_BASE_URL}/api/v1/audit/log`;

/**
 * Mapping of ResourceType to allowed ActionTypes
 * Defines which actions are valid for each resource type
 */
export const RESOURCE_ACTION_TYPE_MAPPING: Record<ResourceType, AuditActionType[]> = {
  // 1. Facility Analysis
  [ResourceType.FACILITY]: [
    AuditActionType.VIEW,
  ],
  [ResourceType.FACILITY_DETAILS]: [
    AuditActionType.VIEW,
  ],
  [ResourceType.FACILITY_LIST]: [
    AuditActionType.VIEW,
  ],

  // 2. Potential Penalties
  [ResourceType.PENALTY_CALCULATION]: [
    AuditActionType.VIEW,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
  ],

  // 4. Continuous Compliance
  [ResourceType.COMPLIANCE_DATA]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
  ],
  [ResourceType.COMPLIANCE_REPORT]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
  ],
  [ResourceType.REGULATORY_REQUIREMENT]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
  ],
  [ResourceType.AUDIT_CHECKLIST]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
  ],

  // 5. Risk Analysis
  [ResourceType.RISK_ANALYSIS]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
  ],

  // 6. Mock Survey
  [ResourceType.MOCK_SURVEY]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.DELETE,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
  ],

  // 7. Survey Viewer from Mock survey screen
  [ResourceType.SURVEY_VIEWER]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.DELETE,
  ],

  // 8. User Management
  [ResourceType.USER_PROFILE]: [
    AuditActionType.USER_INVITE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.USER_ROLE_CHANGE,
    AuditActionType.USER_DEACTIVATE,
    AuditActionType.PASSWORD_RESET,
    AuditActionType.PERMISSION_GRANT,
    AuditActionType.PERMISSION_REVOKE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
  ],
  [ResourceType.USER_LIST]: [
    AuditActionType.USER_INVITE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.USER_ROLE_CHANGE,
    AuditActionType.USER_DEACTIVATE,
    AuditActionType.PASSWORD_RESET,
    AuditActionType.PERMISSION_GRANT,
    AuditActionType.PERMISSION_REVOKE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
  ],
  [ResourceType.USER_INVITATION]: [
    AuditActionType.USER_INVITE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.USER_ROLE_CHANGE,
    AuditActionType.USER_DEACTIVATE,
    AuditActionType.PASSWORD_RESET,
    AuditActionType.PERMISSION_GRANT,
    AuditActionType.PERMISSION_REVOKE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
  ],
  [ResourceType.USER_TYPE]: [
    AuditActionType.USER_INVITE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.USER_ROLE_CHANGE,
    AuditActionType.USER_DEACTIVATE,
    AuditActionType.PASSWORD_RESET,
    AuditActionType.PERMISSION_GRANT,
    AuditActionType.PERMISSION_REVOKE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
  ],
  [ResourceType.USER_SUBTYPE]: [
    AuditActionType.USER_INVITE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.USER_ROLE_CHANGE,
    AuditActionType.USER_DEACTIVATE,
    AuditActionType.PASSWORD_RESET,
    AuditActionType.PERMISSION_GRANT,
    AuditActionType.PERMISSION_REVOKE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
  ],

  // 9. Billings
  [ResourceType.BILLING_INFO]: [
    AuditActionType.VIEW,
    AuditActionType.READ,
    AuditActionType.DOWNLOAD,
  ],
  [ResourceType.PAYMENT]: [
    AuditActionType.VIEW,
    AuditActionType.READ,
    AuditActionType.DOWNLOAD,
  ],
  [ResourceType.PLAN]: [
    AuditActionType.VIEW,
    AuditActionType.READ,
    AuditActionType.DOWNLOAD,
  ],

  // 10. Audit Logs
  [ResourceType.AUDIT_LOG]: [
    AuditActionType.AUDIT_LOG_ACCESS,
    AuditActionType.AUDIT_LOG_EXPORT,
    AuditActionType.VIEW,
    AuditActionType.SEARCH,
    AuditActionType.FILTER,
  ],
  [ResourceType.AUDIT_REPORT]: [
    AuditActionType.AUDIT_LOG_ACCESS,
    AuditActionType.AUDIT_LOG_EXPORT,
    AuditActionType.VIEW,
    AuditActionType.SEARCH,
    AuditActionType.FILTER,
  ],

  // 11. Authentication / Session
  [ResourceType.USER]: [
    AuditActionType.LOGIN,
    AuditActionType.LOGOUT,
    AuditActionType.LOGIN_FAILED,
    AuditActionType.PASSWORD_CHANGE,
    AuditActionType.OTP_SEND,
    AuditActionType.OTP_VERIFY,
    AuditActionType.SESSION_TIMEOUT,
  ],
  [ResourceType.SESSION]: [
    AuditActionType.LOGIN,
    AuditActionType.LOGOUT,
    AuditActionType.LOGIN_FAILED,
    AuditActionType.PASSWORD_CHANGE,
    AuditActionType.OTP_SEND,
    AuditActionType.OTP_VERIFY,
    AuditActionType.SESSION_TIMEOUT,
  ],
  [ResourceType.AUTHENTICATION]: [
    AuditActionType.LOGIN,
    AuditActionType.LOGOUT,
    AuditActionType.LOGIN_FAILED,
    AuditActionType.PASSWORD_CHANGE,
    AuditActionType.OTP_SEND,
    AuditActionType.OTP_VERIFY,
    AuditActionType.SESSION_TIMEOUT,
  ],

  // Backend category enums (returned by API)
  [ResourceType.FACILITY_ANALYSIS]: [AuditActionType.VIEW],
  [ResourceType.POTENTIAL_PENALTIES]: [
    AuditActionType.VIEW,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
  ],
  [ResourceType.EXCLUSION_SANCTION_CHECK]: [
    AuditActionType.VIEW,
    AuditActionType.READ,
    AuditActionType.CREATE,
    AuditActionType.SEARCH,
    AuditActionType.UPLOAD,
  ],
  [ResourceType.CONTINUOUS_COMPLIANCE]: [
    AuditActionType.VIEW,
    AuditActionType.UPLOAD,
    AuditActionType.DOWNLOAD,
    AuditActionType.CONNECT_WITH_AFFABLEFLOW,
    AuditActionType.UPLOAD_FROM_AFFABLE,
  ],
  [ResourceType.USER_MANAGEMENT]: [
    AuditActionType.USER_INVITE,
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.USER_ROLE_CHANGE,
    AuditActionType.USER_DEACTIVATE,
    AuditActionType.PASSWORD_RESET,
    AuditActionType.PERMISSION_GRANT,
    AuditActionType.PERMISSION_REVOKE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
  ],
  [ResourceType.BILLINGS]: [
    AuditActionType.VIEW,
    AuditActionType.READ,
    AuditActionType.DOWNLOAD,
  ],
  [ResourceType.AUDIT_LOGS]: [
    AuditActionType.AUDIT_LOG_ACCESS,
    AuditActionType.AUDIT_LOG_EXPORT,
    AuditActionType.VIEW,
    AuditActionType.SEARCH,
    AuditActionType.FILTER,
  ],
  [ResourceType.AUTHENTICATION_SESSION]: [
    AuditActionType.LOGIN,
    AuditActionType.LOGOUT,
    AuditActionType.LOGIN_FAILED,
    AuditActionType.PASSWORD_CHANGE,
    AuditActionType.OTP_SEND,
    AuditActionType.OTP_VERIFY,
    AuditActionType.SESSION_TIMEOUT,
  ],
  [ResourceType.DISCOVERY_CONTENT]: [
    AuditActionType.CREATE,
    AuditActionType.READ,
    AuditActionType.UPDATE,
    AuditActionType.DELETE,
    AuditActionType.VIEW,
    AuditActionType.SEARCH,
    AuditActionType.FILTER,
    AuditActionType.DOWNLOAD,
    AuditActionType.UPLOAD,
    AuditActionType.SAVE,
  ],
  [ResourceType.NETWORK]: [
    AuditActionType.CREATE,
    AuditActionType.UPDATE,
    AuditActionType.VIEW,
  ],
};

/**
 * Get ResourceTypes for a given category
 * Uses existing ResourceType enum values from RESOURCE_ACTION_TYPE_MAPPING structure
 */
export const getResourceTypesByCategory = (category: string): ResourceType[] => {
  switch (category) {
    case 'FACILITY_ANALYSIS':
      return [ResourceType.FACILITY, ResourceType.FACILITY_DETAILS, ResourceType.FACILITY_LIST];
    case 'POTENTIAL_PENALTIES':
      return [ResourceType.PENALTY_CALCULATION];
    case 'EXCLUSION_SANCTION_CHECK':
      return [ResourceType.EXCLUSION_SANCTION_CHECK];
    case 'CONTINUOUS_COMPLIANCE':
      return [
        ResourceType.COMPLIANCE_DATA,
        ResourceType.COMPLIANCE_REPORT,
        ResourceType.REGULATORY_REQUIREMENT,
        ResourceType.AUDIT_CHECKLIST,
      ];
    case 'RISK_ANALYSIS':
      return [ResourceType.RISK_ANALYSIS];
    case 'MOCK_SURVEY':
      return [ResourceType.MOCK_SURVEY];
    case 'SURVEY_VIEWER':
      return [ResourceType.SURVEY_VIEWER];
    case 'USER_MANAGEMENT':
      return [
        ResourceType.USER_PROFILE,
        ResourceType.USER_LIST,
        ResourceType.USER_INVITATION,
        ResourceType.USER_TYPE,
        ResourceType.USER_SUBTYPE,
      ];
    case 'BILLINGS':
      return [ResourceType.BILLING_INFO, ResourceType.PAYMENT, ResourceType.PLAN];
    case 'AUDIT_LOGS':
      return [ResourceType.AUDIT_LOG, ResourceType.AUDIT_REPORT];
    case 'AUTHENTICATION_SESSION':
      return [ResourceType.USER, ResourceType.SESSION, ResourceType.AUTHENTICATION];
    case 'NETWORK':
      return [ResourceType.NETWORK];
    default:
      return [];
  }
};

/**
 * Labels for Resource Type Categories
 */
export const RESOURCE_CATEGORY_LABELS: Record<string, string> = {
  'FACILITY_ANALYSIS': 'Facility Analysis',
  'POTENTIAL_PENALTIES': 'Potential Penalties',
  'EXCLUSION_SANCTION_CHECK': 'Exclusion / Sanction Check',
  'CONTINUOUS_COMPLIANCE': 'Continuous Compliance',
  'RISK_ANALYSIS': 'Risk Analysis',
  'MOCK_SURVEY': 'Mock Survey',
  'SURVEY_VIEWER': 'Survey Viewer from Mock survey screen',
  'USER_MANAGEMENT': 'User Management',
  'BILLINGS': 'Billings',
  'AUDIT_LOGS': 'Audit Logs',
  'AUTHENTICATION_SESSION': 'Authentication / Session',
  'NETWORK': 'Network',
};

/**
 * Get allowed ActionTypes for a given ResourceType or category string
 * Uses existing RESOURCE_ACTION_TYPE_MAPPING
 * Returns all ActionTypes if no ResourceType is provided
 */
export const getAllowedActionTypes = (resourceType?: ResourceType | string): AuditActionType[] => {
  if (!resourceType) {
    return Object.values(AuditActionType);
  }
  
  // Check if it's a category string - use existing RESOURCE_ACTION_TYPE_MAPPING
  if (typeof resourceType === 'string' && RESOURCE_CATEGORY_LABELS[resourceType]) {
    const resourceTypes = getResourceTypesByCategory(resourceType);
    // Get all unique action types from all resource types in this category using existing mapping
    const allActions = new Set<AuditActionType>();
    resourceTypes.forEach(rt => {
      const actions = RESOURCE_ACTION_TYPE_MAPPING[rt] || [];
      actions.forEach(action => allActions.add(action));
    });
    return Array.from(allActions);
  }
  
  // It's a regular ResourceType - use existing mapping
  return RESOURCE_ACTION_TYPE_MAPPING[resourceType as ResourceType] || [];
};
